import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, ChevronUpIcon, ChevronDownIcon, ArrowPathIcon, TrashIcon, ArrowUpTrayIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from '../../helpers/useDebounce';
import { arrayHasItems, scrollToTop } from '../../helpers/common';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { OverlayGrayBackground } from '../../components/OverlayGrayBackground';
import { useNavigate } from 'react-router-dom';
import { ROUTES, SORT_DIRECTION } from '../../consts/general';
import { Pagination } from '../../components/Pagination';
import { deleteProduct, getAllPC, ProductCostsLoadingKeys, syncProductToShopify, updatePhasedStatus, updateSingleProductInProductCosts } from '../../slices/productCostSlice';
import { productCostsTableHeaders, productCostsTableHiddenHeaders } from '../../consts/productCosts';
import ReactTooltip from 'react-tooltip'
import FilterDropdown from '../../components/FilterDropdown';
import { ACTIVE, enumInventoryPhasedStatus, IN_ACTIVE, PHASED_STATUSES } from '../../consts/inventoryStatus';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BOOLEAN_FILTERS, enumCommonBooleanStatuses, NO, YES } from '../../consts/common';
import { ProductCostSidebar } from '../../components/ProductCostSidebar';
import { ConfrimModal } from '../../components/ConfrimModal';
import { getTotalInventoryValues } from '../../slices/productCostSlice';
import { BulkEditModal } from '../../components/productCosts/BulkEditModal';
import { updateProducts } from '../../slices/productGroupSlice';
import { AddProductModal } from '../../components/productCosts/AddProductModal';
import { usePageReset } from '../../helpers/usePageReset';
 
const ProductCost = ({
  access,
  testId,
  testData
}) => {
  const navigate = useNavigate();
  const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;

  const dispatch = useDispatch();
  const wrapperTableRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(pageParams?.get('search') ||'');
  const [selectedProductCost, setSelectedProductCost] = useState(null);
  const [currentPage, setCurrentPage] = useState(Number(pageParams?.get('page') || 1));
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(Number(pageParams?.get('itemsPerPage') || 100));
  const [sortBy, setSortBy] = useState(pageParams?.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(pageParams?.get('sortDirection')|| '');
  const [csvIsLoading, setCSVIsLoading] = useState(false);
  const [addProductModalOpened, setAddProductModalOpened] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openedBulkEditModal, setOpenedBulkEditModal] = useState(false);
  const [productIdToDelete, setProductIdToDelete] = useState("");
  const [productsToShopifySync, setProductsToShopifySync] = useState(null);
  const [deleteProductModalOpened, setDeleteProductModalOpened] = useState(false);
  const currentSearchValue = useDebounce(searchTerm, 800);
  
  // Auto-reset page to 1 when non-page params change
  const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, 'get:/api/v1/product-costs');
  
  const { 
    productCosts, 
    allProductCosts,
    pagination,
    loadingKeys: loadingKeysPO, 
    loading: loadingPO,
    isFirstLoadComplete,
    totalInventoryValue
  } = useSelector((state) => state.productCosts);
  const [orderedProducts, setOrderedProducts] = useState(
    testId && testData?.orders !== undefined ? testData?.orders : (productCosts || [])
  );
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState(pageParams?.get('phasedStatuses')?.split(',') || [ACTIVE]);
  const [selectedAmazonStatuses, setSelectedAmazonStatuses] = useState(pageParams?.get('amazon')?.split(',') || [YES, NO]);
  const [selectedIsReplenishable, setSelectedIsReplenishable] = useState(pageParams?.get('isReplenishable')?.split(',') || [YES, NO]);
  const [selectedKitStatuses, setSelectedKitStatuses] = useState(pageParams?.get('kit')?.split(',') || [YES, NO]);

  const selectedProductsIds = selectedProducts.map((selectedProduct) => selectedProduct.id);
  const currentFirstLoadComplete = testId && (testData?.pageIsLoaded !== undefined) ? testData?.pageIsLoaded : isFirstLoadComplete;
  useEffect(() => {
    ReactTooltip.rebuild()
  }, [])
  useEffect(() => {
    if (testId) return
    scrollToTop();
  }, [currentPage, currentItemsPerPage]);

  useEffect(() => {
    if (testId) return
    const currentProductCosts = arrayHasItems(productCosts) ? productCosts : [];
    if (selectedProductCost) {
      setSelectedProductCost(currentProductCosts.find((productCost) => productCost.id === selectedProductCost.id));
    };
    setOrderedProducts(currentProductCosts);
  }, [productCosts])

  const handleGetAllPC = async () => {
    dispatch(getAllPC({
      search: currentSearchValue,
      page: currentPage,
      itemsPerPage: currentItemsPerPage,
      phasedStatuses: selectedPhasedStatuses,
      amazon: selectedAmazonStatuses,
      isReplenishable: selectedIsReplenishable,
      kit: selectedKitStatuses,
      sortBy,
      sortDirection
    }));
  };

  useEffect(() => {
    if (testId) return
    
    if (currentPage === 1 && shouldSkipRequest()) {
      setSelectedProducts([]);
      const params = new URLSearchParams({
        search: currentSearchValue,
        page: currentPage,
        itemsPerPage: currentItemsPerPage,
        phasedStatuses: selectedPhasedStatuses,
        amazon: selectedAmazonStatuses,
        isReplenishable: selectedIsReplenishable,
        kit: selectedKitStatuses,
        sortBy,
        sortDirection
      });
      navigate(`${ROUTES.CATALOG}?${params}`, { replace: true });
      return;
    }
    
    handleGetAllPC();
    setSelectedProducts([]);
    const params = new URLSearchParams({
      search: currentSearchValue,
      page: currentPage,
      itemsPerPage: currentItemsPerPage,
      phasedStatuses: selectedPhasedStatuses,
      amazon: selectedAmazonStatuses,
      isReplenishable: selectedIsReplenishable,
      kit: selectedKitStatuses,
      sortBy,
      sortDirection
    });
    navigate(`${ROUTES.CATALOG}?${params}`, { replace: true })
  }, [
    currentSearchValue,
    currentPage,
    currentItemsPerPage,
    selectedPhasedStatuses,
    selectedAmazonStatuses,
    selectedIsReplenishable,
    selectedKitStatuses,
    sortBy,
    sortDirection
  ])

  useEffect(() => {
    dispatch(getTotalInventoryValues());
  }, []);

  const getSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortDirection === SORT_DIRECTION.ASCENDING ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };
  const productCostsIsLoading = () => loadingPO && loadingKeysPO.includes(ProductCostsLoadingKeys.costs);

  const renderPhasedStatusOption = (phase) => (
    <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
  );
  
  const renderIsReplenishableOption = (status) => (
    <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
  );

  const renderAmazonStatusOption = (status) => (
    <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
  );

  const renderKitStatusOption = (status) => (
    <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
  );

  const handleDownloadCSV = async () => {
    try {
      setCSVIsLoading(true);
      const { data } = await axios.get('/api/v1/product-costs', {
        responseType: "blob",
        params: {
          isCSV: "true",
          search: currentSearchValue,
          page: currentPage,
          itemsPerPage: currentItemsPerPage,
          phasedStatuses: selectedPhasedStatuses,
          amazon: selectedAmazonStatuses,
          isReplenishable: selectedIsReplenishable,
          kit: selectedKitStatuses,
          sortBy,
          sortDirection
        }
      });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ProductCosts-All.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setCSVIsLoading(false);
    } catch (error) {
      console.error("Error fetching or downloading CSV:", error);
      toast.error(error?.message?.data || error?.response?.data?.message || "Something went wrong.", { position: "bottom-right" })
      setCSVIsLoading(false);
    }
  };

  const handleChangeProductStatus = async (id, status) => {
    if (access === "edit") {
      await dispatch(updatePhasedStatus({
        productId: id, 
        newPhasedStatus: status,
        phasedStatuses: selectedPhasedStatuses,
        search: currentSearchValue, 
        page: currentPage, 
        itemsPerPage: currentItemsPerPage,
        sortBy,
        sortDirection 
      }));
    }
  };

  const isDeleteProductLoading = () => loadingKeysPO.includes(ProductCostsLoadingKeys.deleteProduct);
  const isSyncProductLandedCostLoading = () => loadingKeysPO.includes(ProductCostsLoadingKeys.syncLandedCost);

  const haveTotalHeaders = totalInventoryValue !== undefined && typeof totalInventoryValue === 'number';

  return (
    <main className="w-full h-full p-8" data-testid={testId}>
      <div className="h-full flex flex-col">
        <h1 className="text-2xl font-semibold mb-6">Catalog</h1>
        
        {/* Search and Filters */}
        <div className="mb-2 flex flex-wrap gap-3 items-center">
          <div className="relative w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 py-2 px-4 block w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {searchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  aria-label="Clear search"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="relative">
            <FilterDropdown
              options={PHASED_STATUSES}
              selectedOptions={selectedPhasedStatuses}
              onChange={setSelectedPhasedStatuses}
              placeholder="Product Status"
              renderOption={renderPhasedStatusOption}
              testId={testId ? `${testId}_phased_filter` : undefined}
            />
          </div>
          
          <div className="relative">
            <FilterDropdown
              options={BOOLEAN_FILTERS}
              selectedOptions={selectedAmazonStatuses}
              onChange={setSelectedAmazonStatuses}
              placeholder="Is Amazon"
              renderOption={renderAmazonStatusOption}
              testId={testId ? `${testId}_amazon_filter` : undefined}
            />
          </div>
          
          <div className="relative">
            <FilterDropdown
              options={BOOLEAN_FILTERS}
              selectedOptions={selectedKitStatuses}
              onChange={setSelectedKitStatuses}
              placeholder="Is Kit"
              renderOption={renderKitStatusOption}
              testId={testId ? `${testId}_kit_filter` : undefined}
            />
          </div>

          <div className="relative">
            <FilterDropdown
              options={BOOLEAN_FILTERS}
              selectedOptions={selectedIsReplenishable}
              onChange={setSelectedIsReplenishable}
              placeholder="Is Replenishable"
              renderOption={renderIsReplenishableOption}
              testId={testId ? `${testId}_replenishable_filter` : undefined}
            />
          </div>

          <button
            className="inline-flex items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={handleDownloadCSV}
          >
            {csvIsLoading ? (
              <LoaderSpinner height='12' width='12' border='2' colorSpinner='black' />
            ) : (
              "Download CSV"
            )}  
          </button>

          {access === "edit" && (
            <button
              className="inline-flex items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={() => {
                setAddProductModalOpened(true);
              }}
            >
              Add Product
            </button>
          )}
        </div>
        {access === "edit" && (
          <div className="mb-6 flex flex-wrap gap-3 items-center">
            <button
              className="inline-flex items-center rounded-md border disabled:bg-gray-200 border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={!arrayHasItems(selectedProducts)}
              onClick={() => {
                setOpenedBulkEditModal(true);
              }}
            >
              Bulk Edit 
            </button>
          </div>
        )}

        {/* Main Content Area */}
        <div className={`flex transition-all duration-300 ease-in-out ${selectedProductCost ? 'gap-4' : ''} flex-grow relative`}>
          {productCostsIsLoading() && <OverlayGrayBackground />}
          
          {/* Product Costs Table */}
          <div className={`transition-all duration-300 h-full w-full`} ref={wrapperTableRef}>
            <div className={`overflow-x-auto ${!haveTotalHeaders && 'shadow-sm rounded-lg border border-gray-200'}`} >
              <table className="w-full action-table">
                <thead>
                <tr className={`bg-gray-50 ${haveTotalHeaders && 'border border-gray-200 border-b-0'}`}>
                    {access === "edit" && (
                      <th 
                        key={`main_actions`}
                        scope="col"
                        className="pt-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[50px]"
                      >
                        <input
                          type="checkbox"
                          onClick={(e) => e.stopPropagation()}
                          checked={
                            arrayHasItems(allProductCosts) &&
                            allProductCosts.length > 0 &&
                            arrayHasItems(selectedProducts) &&
                            selectedProducts.length === allProductCosts.length
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            if (
                              arrayHasItems(selectedProducts) &&
                              arrayHasItems(allProductCosts) &&
                              selectedProducts.length === allProductCosts.length
                            ) {
                              setSelectedProducts([]);
                            } else {
                              setSelectedProducts(allProductCosts);
                            }
                          }}
                          className="w-5 h-5"
                        />
                      </th>
                    )}
                    {Object.values(productCostsTableHeaders).map((header) => {
                      return (
                        <th 
                          key={header.value}
                          scope="col" 
                          className={`pt-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 min-w-[100px] ${
                            productCostsTableHiddenHeaders.includes(header.value) ? 'hidden' : ''
                          } ${header.className || ""}`}
                          onClick={() => {
                            if (sortBy === header.value) {
                              setSortDirection(prev => prev === SORT_DIRECTION.ASCENDING ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING);
                            } else {
                              setSortBy(header.value);
                              setSortDirection(SORT_DIRECTION.ASCENDING);
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {header.label}
                            {header.tooltip && (
                              <span
                                className="inline-flex"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <InformationCircleIcon
                                  className="h-4 w-4 text-gray-500"
                                  data-tip={true}
                                  data-for={`${header.value}_pc_tooltip`}
                                />
                                <ReactTooltip
                                  id={`${header.value}_pc_tooltip`}
                                  html={true}
                                  effect="solid"
                                  place="bottom"
                                  className="text-xs normal-case"
                                >
                                  {header.tooltip}
                                </ReactTooltip>
                              </span>
                            )}
                            {getSortIcon(header.value)}
                          </div>
                        </th>
                      )
                    })}
                    {access === "edit" && (
                      <th
                        key={`edit_actions`}
                        scope="col"
                        className="pt-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[50px]"
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                  {haveTotalHeaders && (
                    <tr className="bg-gray-50">
                      <th colSpan="1" className='!bg-gray-50 !static border-l border-gray-200'></th>
                      {Object.values(productCostsTableHeaders).map((header) => {
                        if (productCostsTableHiddenHeaders.includes(header.value)) return null;
                        if (header.value === 'inventoryValue') {
                          return (
                            <th key={header.value} colSpan="1" className="text-center py-1 text-xs font-semibold uppercase">
                              <div className="flex items-center justify-center gap-2">
                                {`$${Number(totalInventoryValue)?.toLocaleString('en-US', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                                {productCostsTableHeaders?.inventoryValue?.tooltip && (
                                  <span
                                    className="inline-flex"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <InformationCircleIcon
                                      className="h-4 w-4 text-gray-500"
                                      data-tip={true}
                                      data-for="inventoryValue_total_pc_tooltip"
                                    />
                                    <ReactTooltip
                                      id="inventoryValue_total_pc_tooltip"
                                      html={true}
                                      effect="solid"
                                      place="bottom"
                                      className="text-xs normal-case"
                                    >
                                      {productCostsTableHeaders.inventoryValue.tooltip}
                                    </ReactTooltip>
                                  </span>
                                )}
                              </div>
                            </th>
                          )
                        } else {
                          return (
                            <th key={header.value} colSpan="1"></th>
                          )
                        }
                      })}
                      <th colSpan="1" className='!bg-gray-50 !static  border-r border-gray-200'></th>
                    </tr>
                  )}

                </thead>
                <tbody className={`${haveTotalHeaders && 'border border-gray-200'} bg-white`}>
                  {!currentFirstLoadComplete && (
                    <tr>
                      <td colSpan="13" className="text-center py-12">
                        <LoaderSpinner width='70' height='70' border='3' colorSpinner='black' testId={testId ? `${testId}_table_loader` : undefined} />
                      </td>
                    </tr>
                  )}
                  
                  {arrayHasItems(orderedProducts) ? (
                    orderedProducts.map((product, index) => {
                      return (
                        <tr 
                          key={`${product.id}_${index}_${product.statusBeingPhasedOut}`} 
                          className={`
                            border-b border-gray-200 last:border-b-0 hover:bg-blue-50 
                            cursor-pointer
                            ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            ${selectedProductCost?.id === product.id ? '!bg-blue-50' : ''}
                            ${product.statusBeingPhasedOut === IN_ACTIVE ? "text-gray-400" : ""}
                          `}
                          onClick={() => setSelectedProductCost(product)}
                        >
                          {access === "edit" && (
                            <td className="py-2 px-4 text-sm text-gray-600">
                              <input
                                type="checkbox"
                                checked={selectedProducts.some((p) => p.id === product.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setSelectedProducts((prev) => {
                                    const isAlreadySelected = prev.some((p) => p.id === product.id);
                                    if (isAlreadySelected) {
                                      return prev.filter((p) => p.id !== product.id);
                                    } else {
                                      return [...prev, product];
                                    }
                                  });
                                }}
                                className="w-5 h-5"
                              />
                            </td>
                          )}
                          <td className="py-2 px-4 text-sm text-gray-600">
                            <div className="w-12 h-12 relative rounded-md overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                              <img
                                src={product.productImage || 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image'}
                                alt={product.productName}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://placehold.co/40x40/e6e6e6/cccccc?text=No+Image';
                                }}
                              />
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium">
                            <article>
                              <div className="flex items-center flex-row gap-2">
                                <h3 className="text-base font-medium">
                                  {product.productName}
                                </h3>
                                {product.isVirtual && (
                                  <>
                                    <InformationCircleIcon
                                      className="h-5 w-5 text-gray-500 flex-shrink-0"
                                      data-tip={true}
                                      data-for={`${product.id}_product_name_tooltip_${index}_${product.productName}`}
                                    />
                                    <ReactTooltip
                                      id={`${product.id}_product_name_tooltip_${index}_${product.productName}`}
                                      html={true}
                                      effect="solid"
                                      place="bottom"
                                      className="text-xs normal-case"
                                    >
                                      Virtual skus do not have cost, we do not track inventory
                                    </ReactTooltip>
                                  </>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                SKU: {product.sku}
                              </div>
                            </article>
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap hidden">
                            {product.sku}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.productBarcode}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap hidden">
                            Kingship
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.unitCost ? `$${product.unitCost}` : "N/A"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.shippingCost ? `$${product.shippingCost}` : "N/A"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.tariffCost ? `$${product.tariffCost}` : "N/A"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.totalLandedCost ? `$${product.totalLandedCost}` : "N/A"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                          <div className='flex items-center justify-between gap-2'>
                              <span>
                                {product.weightedCost ? `$${product.weightedCost}` : "N/A"}
                              </span>
                              {arrayHasItems(product.shopifyRelations) && product.weightedCost && (
                                <button
                                  className="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded border border-black disabled:opacity-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProductsToShopifySync(product.shopifyRelations || null);
                                  }}
                                >
                                  {(isSyncProductLandedCostLoading() && (productsToShopifySync || []).some((item) => item?.product_id === product.id)) ? (
                                    <ArrowPathIcon className="animate-spin h-4 w-4" />
                                  ) : (
                                    <>
                                      <span>Sync</span>
                                      <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {/* <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${product.isAmazon ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                              {product.isAmazon ? "Yes" : "No"}
                            </span> */}
                            {product.onHand || "-"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.onOrder || "-"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {/* <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(product.statusBeingPhasedOut)}`}>
                              {enumInventoryPhasedStatus[product.statusBeingPhasedOut] || null}
                            </span> */}
                            {product.inventoryValue ? `$${product.inventoryValue}` : "N/A"}
                          </td>
                          <td className="py-3 px-4 text-sm whitespace-nowrap">
                            {product.hasGroup ? "Y" : "N"}
                          </td>
                          {access === "edit" && (
                            <td className={`py-3 px-4 text-sm whitespace-nowrap ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                              <div className="flex justify-end gap-4">
                                <button
                                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded border border-black disabled:opacity-50"
                                  title={`Delete product ${product.productName}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setProductIdToDelete(product.id);
                                    setDeleteProductModalOpened(true);
                                  }}
                                >
                                  {(isDeleteProductLoading() && productIdToDelete === product.id) ? (
                                    <ArrowPathIcon className="animate-spin h-4 w-4" />
                                  ) : (
                                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                  )}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    currentFirstLoadComplete && (
                      <tr data-testid={testId ? `${testId}_empty_table` : undefined}>
                        <td colSpan="13" className="px-6 py-12 text-sm text-gray-500 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
                            <p className="mt-1 text-sm text-gray-500">No product costs match the current filters.</p>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <ProductCostSidebar
            key={`${selectedProductCost?.id}_${selectedProductCost?.statusBeingPhasedOut}`}
            pc={selectedProductCost}
            access={access}
            isOpen={!!selectedProductCost}
            updateSingleProductCallback={async () => {
              await dispatch(updateSingleProductInProductCosts({
                productId: selectedProductCost?.id,
                search: currentSearchValue,
                phasedStatuses: selectedPhasedStatuses,
                isReplenishable: selectedIsReplenishable,
                amazon: selectedAmazonStatuses,
                kit: selectedKitStatuses,
              })).unwrap();
              dispatch(getTotalInventoryValues());
            }}
            findProductBySkuCallback={(sku) => {
              setSearchTerm(sku);
              setCurrentPage(1);
              setSelectedPhasedStatuses([ACTIVE]);
              setSelectedKitStatuses([YES, NO]);
              setSelectedAmazonStatuses([YES, NO]);
              setSelectedIsReplenishable([YES, NO]);
            }}
            changeProductStatusCallback={async (id, status) => {
              await handleChangeProductStatus(id, status);
              dispatch(getTotalInventoryValues());
            }}
            onClose={() => setSelectedProductCost(null)}
          />
        </div>
        
        {/* Pagination */}
        {currentFirstLoadComplete && (
          <div className="mt-6">
            <Pagination
              currentItemsPerPage={currentItemsPerPage}
              rangeRows={pagination.rangeRows}
              additionalRanges={[
                { value: 250, label: "250" },
                { value: 500, label: "500" },
              ]}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              isTheLastPage={pagination.isTheLastPage}
              totalPages={pagination.totalPages}
              setCurrentItemsPerPage={setCurrentItemsPerPage}
              testId={testId}
            />
          </div>
        )}

        <AddProductModal
          isOpen={access === "edit" && addProductModalOpened}
          onClose={() => {
            setAddProductModalOpened(false);
          }}
        />

        <BulkEditModal
          isOpen={access === "edit" && openedBulkEditModal}
          products={selectedProducts}
          onAcceptActions={async (actions) => {
            await dispatch(updateProducts({
              productIds: selectedProductsIds,
              actions,
              type: 'actions'
            }));
            setSelectedProducts([]);
            setOpenedBulkEditModal(false);
            handleGetAllPC();
          }}
          onAddVariant={async (variant) => {
            const sku = variant?.sku || "";
            await dispatch(updateProducts({
              productIds: selectedProductsIds,
              sourceSku: sku,
              type: 'variants'
            }));
            setSelectedProducts([]);
            setOpenedBulkEditModal(false);
            handleGetAllPC();
          }}
          onAddGroup={async (group) => {
            const groupId = group?.id || "";
            await dispatch(updateProducts({
              productIds: selectedProductsIds,
              groupId,
              type: 'groups'
            }));
            setSelectedProducts([]);
            setOpenedBulkEditModal(false);
            handleGetAllPC();
          }}
          isReplenishable={true}
          onClose={() => {
            setOpenedBulkEditModal(false);
          }}
        />

        <ConfrimModal
          isOpen={access === "edit" && deleteProductModalOpened}
          title={"Are you sure want to delete this product?"}
          confirmButtonTitle={"Delete"}
          isLoading={isDeleteProductLoading()}
          submitClick={async () => {
            setDeleteProductModalOpened(false)
            await dispatch(deleteProduct({ productId: productIdToDelete })).unwrap();
            await handleGetAllPC();
            setProductIdToDelete("")
          }}
          cancelClick={() => {
            setDeleteProductModalOpened(false)
            setProductIdToDelete("")
          }}
        />
        <ConfrimModal
          isOpen={access === "edit" && !!productsToShopifySync}
          title={"Are you sure want to sync this product to Shopify?"}
          confirmButtonTitle={"Sync"}
          isLoading={isSyncProductLandedCostLoading()}
          submitClick={async () => {
            if (arrayHasItems(productsToShopifySync)) {
              await dispatch(syncProductToShopify({ 
                productsToShopifySync
              })).unwrap();
            }
            setProductsToShopifySync(null);
          }}
          cancelClick={() => {
            setProductsToShopifySync(null);
          }}
        />
      </div>
    </main>
  );
}

export default ProductCost;